import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { TENANT } from "@types/tenant";
import { ADMINUSER } from "@types/adminUser";
import { CONTRACT } from "@types/contract";
import { BlankTenantData } from "@templates/BlankTenantData";
import { BlankAdminUserData } from "@templates/BlankAdminUserData";
import { getAppData } from "@services/API/Requests/AppRequests";
import { postTenantAdminData } from "@services/API/Requests/TenantRequests";
import { checkRealmExists } from "@services/KeyCloak/KeyCloak";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { GenerateRandomPassword } from "@services/Utilities";
import { TENANT_PATHS } from "@constants/NavigationPaths";
import { error_message } from "@constants/Errors";
import { APP_NAMES, CONTRACT_STATUS } from "@constants/Enums";
import messages from "@constants/Messages";
import { validateDates } from "@components/Forms/common/utilities/tenant/ValidateDates";
import { validateUnitPrice } from "@components/Forms/common/utilities/tenant/ValidateUnitPrice";

export const useCreateTenantForm = () => {
  const navigate = useNavigate();
  const useHazumApi = import.meta.env.VITE_USE_HAZUM_API === "true";

  const [tenantData, setTenantData] = useState<TENANT>(BlankTenantData);
  const [adminUserData, setAdminUserData] =
    useState<ADMINUSER>(BlankAdminUserData);
  const [contractData, setContractData] = useState<CONTRACT[]>([]);
  const [appData, setAppData] = useState<any[]>([]);

  const [hasYobelyLicense, setHasYobelyLicense] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [error, setError] = useState("");
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const [errors, setErrors] = useState({
    tenant_code: false,
    tenant_name: false,
    login_id: false,
    login_id_length: false,
    login_password: false,
  });

  const [dateError, setDateError] = useState(false);
  const [unitPriceError, setUnitPriceError] = useState(false);

  useEffect(() => {
    const fetchAppData = async () => {
      setLoading(true);
      await getAppData(setAppData, setError, setLoading);
      setLoading(false);
    };
    fetchAppData();
  }, []);

  const successToast = (msg: string) => toast.success(msg);
  const errorToast = (msg: string) => toast.error(msg);

  const updateTenantData = (key: keyof TENANT, value: string | boolean) => {
    setTenantData((prev) => ({ ...prev, [key]: value }));
  };

  const updateAdminData = (key: keyof ADMINUSER, value: string | boolean) => {
    setAdminUserData((prev) => ({ ...prev, [key]: value }));
  };

  const updateContractData = (
    app_id: number,
    key: keyof CONTRACT,
    value: any,
  ) => {
    setContractData((prev) => {
      const updated = prev.map((contract) =>
        contract.app_id === app_id ? { ...contract, [key]: value } : contract,
      );
      const hasYobely = updated.some((contract) => {
        const app = appData.find((a) => a.id === contract.app_id);
        return (
          app?.name === APP_NAMES.YOBELY &&
          contract.status === CONTRACT_STATUS.ACTIVE
        );
      });
      setHasYobelyLicense(hasYobely);
      return updated;
    });
  };

  const updateError = (field: string, value: boolean) => {
    setErrors((prev) => ({ ...prev, [field]: value }));
  };

  const onCancel = () => navigate(TENANT_PATHS.listCompanies);

  const handleModalConfirm = () => {
    const newPassword = GenerateRandomPassword(12);
    if (adminUserData) adminUserData.login_password = newPassword;
    passwordChangeSuccessToast();
    setPasswordModalOpen(false);
  };

  const passwordChangeSuccessToast = () =>
    toast.success(messages.password.init_success);

  const verifyFields = () => !Object.values(errors).some((val) => val === true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validFields = verifyFields();
    const realmExists = await checkRealmExists(tenantData?.code);

    if (!validFields) {
      errorToast(error_message.generic.bad_field);
      return;
    }
    if (adminUserData?.login_password === "") {
      errorToast(error_message.password.must_init);
      return;
    }
    if (realmExists) {
      errorToast(error_message.tenant.exists(tenantData?.code));
      setError(error_message.tenant.exists(tenantData?.code));
      return;
    }
    if (
      !validateDates(errorToast, setError, setDateError, contractData) ||
      !validateUnitPrice(errorToast, setUnitPriceError, contractData)
    )
      return;

    postTenantAdminData(
      tenantData,
      adminUserData,
      contractData,
      setRequestLoading,
      setError,
    )
      .then((response) => {
        if (response.status === 200) {
          successToast(response.message);
          setTimeout(() => navigate(TENANT_PATHS.listCompanies), 1000);
        } else {
          const msg = TranslateErrorMessage(response.message);
          setError(msg);
          errorToast(msg);
        }
      })
      .catch((error) => {
        const msg = TranslateErrorMessage(error.message);
        setError(msg);
        errorToast(msg);
      });
  };

  const handleKeycloakGroupsCheckboxChange = useCallback(
    (selectedGroup) => {
      const existingGroups = tenantData?.keycloak_groups?.tenant_groups || [];
      const groupExists = existingGroups.some(
        (g: any) => g.name === selectedGroup.name,
      );

      const updatedGroups = groupExists
        ? existingGroups.filter((g: any) => g.name !== selectedGroup.name)
        : [
            ...existingGroups,
            {
              id: selectedGroup.id || 0,
              name: selectedGroup.name,
              parent: selectedGroup.parent,
              full_path: selectedGroup.full_path,
            },
          ];

      updateTenantData("keycloak_groups", {
        ...tenantData.keycloak_groups,
        tenant_groups: updatedGroups,
      });
    },
    [tenantData],
  );

  return {
    tenantData,
    adminUserData,
    contractData,
    appData,
    hasYobelyLicense,
    loading,
    requestLoading,
    error,
    passwordModalOpen,
    warningModalOpen,
    errors,
    useHazumApi,
    setContractData,
    setPasswordModalOpen,
    handleSubmit,
    setLoading,
    setError,
    updateTenantData,
    updateAdminData,
    updateContractData,
    updateError,
    handleModalConfirm,
    handleKeycloakGroupsCheckboxChange,
    handleOpenWarningModal: () => setWarningModalOpen(true),
    handleCloseWarningModal: () => setWarningModalOpen(false),
    onCancel,
  };
};
